import Image from 'next/image'
import React, { ChangeEvent, useState, useRef } from 'react'
import { RiExpandDiagonalFill } from 'react-icons/ri'

import ImageFocusModal from '../private/image-focus-modal'

type TCheckbox = {
  readonly values: string[]
  readonly options: {
    title: string
    value: string
    answerImageUrl: string | null
    isExclusiveOption: boolean
  }[]
  readonly onChange: (value: string[]) => void
  readonly error?: string
}

export default function Checkbox({ values, options, onChange, error }: TCheckbox) {
  const [checkedValues, setCheckedValues] = useState<string[]>(values)
  const checkboxesRef = useRef<(HTMLInputElement | null)[]>([])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [imageFocusDetails, setImageFocusDetails] = useState<{
    src: string | null
    alt: string | null
  }>({ src: null, alt: null })

  const hasImage = options.some((option) => option.answerImageUrl)
  // const hasExclusiveOption = options.some((option) => option.isExclusiveOption)

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target

    const currentOption = options.find((option) => option.value === value)

    if (!currentOption) {
      return
    }

    let updatedValues: string[]

    if (currentOption.isExclusiveOption) {
      if (checked) {
        updatedValues = [value]
      } else {
        updatedValues = checkedValues.filter((v) => v !== value)
      }
    } else {
      if (checked) {
        updatedValues = [
          ...checkedValues.filter((v) => {
            const option = options.find((opt) => opt.value === v)
            return option && !option.isExclusiveOption
          }),
          value,
        ]
      } else {
        updatedValues = checkedValues.filter((v) => v !== value)
      }
    }

    setCheckedValues(updatedValues)
    onChange(updatedValues)
  }

  const handleDivClick = (index: number) => {
    if (checkboxesRef.current[index]) {
      checkboxesRef.current[index]?.click()
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setImageFocusDetails({ src: null, alt: null })
  }

  const onClickExpand = ({
    e,
    src,
  }: {
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    src: string
  }) => {
    setIsModalOpen(true)
    setImageFocusDetails({
      src: src,
      alt: 'answer-option-image',
    })
    e.stopPropagation()
  }

  return (
    <div className={`${hasImage && 'md:grid-cols-2'} grid grid-cols-1 gap-5 overflow-hidden`}>
      {options.map((res, index) => (
        <div key={res.value}>
          <button
            type='button'
            className={`w-full cursor-pointer items-center rounded-lg border pt-3 ${
              values.includes(res.value) ? 'border-[#329BFF] bg-[#E9F1FF]' : 'border-pop-gray-5'
            }`}
            onClick={() => handleDivClick(index)}
          >
            <div className='mx-3 mb-3 flex flex-row gap-2'>
              <input
                type='checkbox'
                value={res.value}
                checked={checkedValues.includes(res.value)}
                onChange={handleOnChange}
                ref={(el) => {
                  if (el) {
                    checkboxesRef.current[index] = el
                  }
                }}
                onClick={() => handleDivClick(index)}
              />
              <span className='sentry-mask text-left'>{res.title}</span>
            </div>
            {hasImage && (
              <div className='relative flex h-[280px] w-full flex-row justify-center rounded-br-lg bg-neutral-200'>
                {res?.answerImageUrl && (
                  <>
                    <div className='relative h-[280px] w-full'>
                      <Image
                        src={res.answerImageUrl}
                        alt={`image-${res}`}
                        layout='fill'
                        objectFit='contain'
                        priority
                      />
                    </div>
                    <button
                      className='absolute bottom-0 right-0 rounded-br-lg bg-[#333333] p-2 text-white opacity-50'
                      type='button'
                      onClick={(e) => onClickExpand({ e, src: res.answerImageUrl ?? '' })}
                    >
                      <RiExpandDiagonalFill size={20} />
                    </button>
                  </>
                )}
              </div>
            )}
          </button>
        </div>
      ))}
      {!!error && <span className='w-full text-left text-xs text-pop-error'>{error}</span>}
      <ImageFocusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        src={imageFocusDetails.src ?? ''}
        alt={imageFocusDetails.alt ?? ''}
      />
    </div>
  )
}
